import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash-es';
import { BusinessSignOffService } from './business-sign-off.service';
import { BusinessSignOffEditModalComponent } from './business-sign-off-edit-modal/business-sign-off-edit-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface SignOffItem {
  PARENT_ME_CODE: string;
  PARENT_ME_NAME: string;
  SIGN_OFF_GROUP_LABEL: string;
  SIGN_OFF_DESC: string;
  SIGN_OFF_ID: number;
  SIGNED_OFF: number;
  SIGNED_OFF_BY: string;
  DATE_SIGNED_OFF: string;
}

interface MonthFilter {
  name: string;
  value: string;
}

@Component({
    selector: 'gtw-business-sign-off',
    templateUrl: './business-sign-off.component.html',
    styleUrls: ['./business-sign-off.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BusinessSignOffComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isLoading = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  filterParams: any = {};
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;
  rowData: any = {};

  // Component state
  signOffLoading = false;
  filtersSelected = false;
  showFormLoader = false;

  // Data
  allData: SignOffItem[] = [];
  filteredList: SignOffItem[] = [];

  // Month filter
  monthFilterList: MonthFilter[] = [];
  monthSearch: MonthFilter | null = null;

  // BU multi-select filter
  buFilterValues: { name: string; value: string }[] = [];
  buFilterModel: string[] = [];

  // BU dropdown state
  buDropdownOpen = false;

  // Search
  searchText = '';

  // Displayed list (computed from filteredList + searchText)
  displayedList: SignOffItem[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog,
    private businessSignOffService: BusinessSignOffService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryLoad();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryLoad();
    }));

    this.tryLoad();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};

    this.globalService = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.appService.USER_SETTINGS = this.globalService.userSettingsData;
    this.appService.SERVER_CONFIG = this.globalService.serverConfigData;
    this.GENERAL_CONFIG = this.globalService.generalConfigData;
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);

    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  /**
   * Called every time filterParams changes.
   * Checks if we have enough data to make the API call, then loads.
   */
  private tryLoad(): void {
    if (this.isLoading) {
      return;
    }
    // Guard: explicit base_url check prevents HTTP calls with undefined URL
    if (!this.resolveApiBase()) {
      console.log('[BusinessSignOff] base_url not yet available, skipping tryLoad');
      return;
    }
    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }
    this.loadSignOffList();
  }

  loadSignOffList(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.signOffLoading = true;
    this.cdr.detectChanges();

    const params: any = {
      ...this.filterParams,
      jcd_key: this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key
    };

    this.businessSignOffService.getSignOffList(this.resolveApiBase(), params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('[BusinessSignOff] API response:', data);
        console.log('[BusinessSignOff] jsonObject length:', data.jsonObject ? data.jsonObject.length : 'null/undefined');
        if (data.callSuccess === '1' || data.callSuccess === 1) {
          this.allData = data.jsonObject || [];

          // Build month filter list from unique SIGN_OFF_GROUP_LABEL
          const uniqueMonths = _.uniqBy(this.allData, 'SIGN_OFF_GROUP_LABEL');
          this.monthFilterList = uniqueMonths.map((item: any) => ({
            name: item.SIGN_OFF_GROUP_LABEL,
            value: item.SIGN_OFF_GROUP_LABEL
          }));

          // Default to first month
          if (this.monthFilterList.length > 0) {
            this.monthSearch = this.monthFilterList[0];
          } else {
            this.monthSearch = null;
          }

          // Build BU filter
          this.buFilterValues = _.uniqBy(this.allData, 'PARENT_ME_CODE').map((item: any) => ({
            name: item.PARENT_ME_NAME,
            value: item.PARENT_ME_CODE
          }));
          this.buFilterModel = [];

          this.filterByMonth();
          this.filtersSelected = true;
          console.log('[BusinessSignOff] allData:', this.allData.length, 'monthFilterList:', this.monthFilterList, 'monthSearch:', this.monthSearch, 'filteredList:', this.filteredList.length, 'displayedList:', this.displayedList.length);
        } else if (data.errorMessage && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Request failed', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }

        this.signOffLoading = false;
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading sign-off list:', error);
        this._snackBar.open('Error loading sign-off data', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        this.signOffLoading = false;
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  filterByMonth(): void {
    this.filteredList = [];
    if (this.monthSearch && this.monthSearch.value) {
      this.filteredList = this.allData.filter(
        (item: SignOffItem) => item.SIGN_OFF_GROUP_LABEL === this.monthSearch!.value
      );

      // Apply BU filter if selected
      if (this.buFilterModel && this.buFilterModel.length > 0) {
        this.filteredList = this.filteredList.filter(
          (item: SignOffItem) => this.buFilterModel.indexOf(item.PARENT_ME_CODE) !== -1
        );
      }
    }
    this.updateDisplayedList();
  }

  onMonthChange(): void {
    this.filterByMonth();
  }

  onBuFilterChange(): void {
    this.filterByMonth();
  }

  toggleBuFilter(value: string): void {
    const idx = this.buFilterModel.indexOf(value);
    if (idx === -1) {
      this.buFilterModel = [...this.buFilterModel, value];
    } else {
      this.buFilterModel = this.buFilterModel.filter(v => v !== value);
    }
    this.onBuFilterChange();
  }

  onSearchChange(): void {
    this.updateDisplayedList();
  }

  private updateDisplayedList(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.displayedList = [...this.filteredList];
    } else {
      const search = this.searchText.toLowerCase();
      this.displayedList = this.filteredList.filter(
        (item: SignOffItem) => item.PARENT_ME_NAME.toLowerCase().includes(search)
      );
    }
    this.cdr.detectChanges();
  }

  saveSignOff(item: SignOffItem): void {
    const jsonObj = {
      parent_me_code: item.PARENT_ME_CODE,
      parent_me_name: item.PARENT_ME_NAME,
      signed_off: item.SIGNED_OFF == 1 ? 1 : 0,
      signed_off_desc: item.SIGN_OFF_DESC,
      sign_off_id: item.SIGN_OFF_ID,
      signed_off_by: this.appService.USER_SETTINGS?.user?.sso_id || ''
    };

    const params = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key,
      jsonObj: JSON.stringify(jsonObj)
    };

    this.businessSignOffService.saveSignOffCmt(this.resolveApiBase(), params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && (data.callSuccess == 1 || data.callSuccess === '1')) {
          this._snackBar.open('Business Signed Off Successfully...', 'Close', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
          this.reloadList();
        } else {
          this._snackBar.open(data.errorMessage || 'Error saving sign-off', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          this.reloadList();
        }
      },
      (error: any) => {
        console.error('Error saving sign-off:', error);
        this._snackBar.open('Error saving sign-off', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  onCheckboxChange(item: SignOffItem): void {
    item.SIGNED_OFF = item.SIGNED_OFF == 1 ? 0 : 1;
    this.saveSignOff(item);
  }

  openEditModal(item: SignOffItem): void {
    const dialogRef = this.dialog.open(BusinessSignOffEditModalComponent, {
      width: '500px',
      disableClose: true,
      panelClass: 'sign-off-edit-dialog',
      data: {
        parentMeCode: item.PARENT_ME_CODE,
        parentMeName: item.PARENT_ME_NAME,
        signOffDesc: item.SIGN_OFF_DESC,
        signOffId: item.SIGN_OFF_ID,
        signedOff: item.SIGNED_OFF,
        baseUrl: this.resolveApiBase(),
        filterParams: this.filterParams,
        globalService: this.globalService
      }
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result === 'saved') {
        this.reloadList();
      }
    });
  }

  private reloadList(): void {
    this.isLoading = false;
    this.loadSignOffList();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
